import {useCallback, useEffect, useRef, useState} from 'react'
import {Pressable, View} from 'react-native'
import Animated, {FadeIn, FadeOut} from 'react-native-reanimated'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {clamp} from '#/lib/numbers'
import {PressableWithHover} from '#/view/com/util/PressableWithHover'
import {atoms as a, useTheme, web} from '#/alf'
import {
  ArrowsDiagonalIn_Stroke2_Corner0_Rounded as ArrowsInIcon,
  ArrowsDiagonalOut_Stroke2_Corner0_Rounded as ArrowsOutIcon,
} from '#/components/icons/ArrowsDiagonal'
import {Mute_Stroke2_Corner0_Rounded as MuteIcon} from '#/components/icons/Mute'
import {Pause_Filled_Corner0_Rounded as PauseIcon} from '#/components/icons/Pause'
import {Play_Filled_Corner0_Rounded as PlayIcon} from '#/components/icons/Play'
import {SpeakerVolumeFull_Stroke2_Corner0_Rounded as UnmuteIcon} from '#/components/icons/Speaker'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'
import {IS_NATIVE, IS_WEB} from '#/env'
import type {VideoPlayerControlsProps} from './types'

export function VideoPlayerControls({
  playing,
  currentTime,
  duration,
  buffering,
  muted,
  visible,
  onPlayPause,
  onSeek,
  onToggleMute,
  onToggleFullscreen,
  onInteractionStart,
  onInteractionEnd,
}: VideoPlayerControlsProps) {
  const t = useTheme()
  const {_} = useLingui()
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isSeeking, setIsSeeking] = useState(false)
  const [seekPosition, setSeekPosition] = useState(0)
  const barRef = useRef<HTMLDivElement>(null)

  // Track fullscreen state
  useEffect(() => {
    if (!IS_WEB) return

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const handleToggleFullscreen = useCallback(() => {
    onToggleFullscreen?.()
  }, [onToggleFullscreen])

  const seek = useCallback(
    (evt: React.PointerEvent<HTMLDivElement>) => {
      if (!barRef.current) return
      const {left, width} = barRef.current.getBoundingClientRect()
      const x = evt.clientX
      const percent = clamp((x - left) / width, 0, 1) * duration
      onSeek(percent)
      setSeekPosition(percent)
    },
    [duration, onSeek],
  )

  const onPointerDown = useCallback(
    (evt: React.PointerEvent<HTMLDivElement>) => {
      const target = evt.target
      if (target instanceof Element) {
        evt.preventDefault()
        target.setPointerCapture(evt.pointerId)
        setIsSeeking(true)
        seek(evt)
        onInteractionStart?.()
      }
    },
    [seek, onInteractionStart],
  )

  const onPointerMove = useCallback(
    (evt: React.PointerEvent<HTMLDivElement>) => {
      if (isSeeking) {
        evt.preventDefault()
        seek(evt)
      }
    },
    [isSeeking, seek],
  )

  const onPointerUp = useCallback(
    (evt: React.PointerEvent<HTMLDivElement>) => {
      const target = evt.target
      if (isSeeking && target instanceof Element) {
        evt.preventDefault()
        target.releasePointerCapture(evt.pointerId)
        setIsSeeking(false)
        onInteractionEnd?.()
      }
    },
    [isSeeking, onInteractionEnd],
  )

  const progress = isSeeking ? seekPosition : currentTime
  const progressPercent = duration > 0 ? (progress / duration) * 100 : 0

  const showControls = visible || !playing || isSeeking

  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(200)}
      style={[
        a.absolute,
        a.inset_0,
        a.justify_end,
        {opacity: showControls ? 1 : 0},
        web({transition: 'opacity 0.2s ease-in-out'}),
      ]}
      pointerEvents={showControls ? 'auto' : 'none'}>
      {/* Tap area for play/pause */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={playing ? _(msg`Pause video`) : _(msg`Play video`)}
        onPress={onPlayPause}
        style={[a.flex_1]}
      />

      {/* Buffering indicator */}
      {buffering && (
        <View
          style={[a.absolute, a.inset_0, a.justify_center, a.align_center]}
          pointerEvents="none">
          <Loader fill={t.palette.white} size="lg" />
        </View>
      )}

      {/* Play/pause overlay icon */}
      {!playing && !buffering && (
        <View
          style={[a.absolute, a.inset_0, a.justify_center, a.align_center]}
          pointerEvents="none">
          <View
            style={[
              a.rounded_full,
              a.justify_center,
              a.align_center,
              {
                width: 64,
                height: 64,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
              },
            ]}>
            <PlayIcon fill={t.palette.white} width={32} />
          </View>
        </View>
      )}

      {/* Bottom controls bar */}
      <View
        style={[
          a.w_full,
          a.px_sm,
          a.pb_sm,
          web({
            background:
              'linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.7))',
          }),
        ]}>
        {/* Progress bar (web only for now) */}
        {IS_WEB && duration > 0 && (
          <View style={[{height: 24}, a.justify_center, a.mb_xs]}>
            <div
              ref={barRef}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                cursor: isSeeking ? 'grabbing' : 'pointer',
                padding: '8px 0',
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}>
              <View
                style={[
                  a.w_full,
                  a.rounded_full,
                  a.overflow_hidden,
                  {backgroundColor: 'rgba(255, 255, 255, 0.3)', height: 4},
                ]}>
                <View
                  style={[
                    a.h_full,
                    {backgroundColor: t.palette.white},
                    {width: `${progressPercent}%`},
                  ]}
                />
              </View>
              {/* Seek handle */}
              <View
                style={[
                  a.absolute,
                  {
                    left: `${progressPercent}%`,
                    marginLeft: -6,
                    width: 12,
                    height: 12,
                  },
                  a.rounded_full,
                  {backgroundColor: t.palette.white},
                  {transform: [{scale: isSeeking ? 1.2 : 1}]},
                ]}
              />
            </div>
          </View>
        )}

        {/* Control buttons */}
        <View style={[a.flex_row, a.align_center, a.gap_sm]}>
          {/* Play/Pause button */}
          <ControlButton
            active={playing}
            activeLabel={_(msg`Pause`)}
            inactiveLabel={_(msg`Play`)}
            activeIcon={PauseIcon}
            inactiveIcon={PlayIcon}
            onPress={onPlayPause}
          />

          {/* Time display */}
          {duration > 0 && (
            <Text
              style={[
                a.text_sm,
                {color: t.palette.white, fontVariant: ['tabular-nums']},
              ]}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Text>
          )}

          <View style={a.flex_1} />

          {/* Mute button */}
          <ControlButton
            active={muted}
            activeLabel={_(msg`Unmute`)}
            inactiveLabel={_(msg`Mute`)}
            activeIcon={MuteIcon}
            inactiveIcon={UnmuteIcon}
            onPress={onToggleMute}
          />

          {/* Fullscreen button (web only) */}
          {IS_WEB && onToggleFullscreen && (
            <ControlButton
              active={isFullscreen}
              activeLabel={_(msg`Exit fullscreen`)}
              inactiveLabel={_(msg`Enter fullscreen`)}
              activeIcon={ArrowsInIcon}
              inactiveIcon={ArrowsOutIcon}
              onPress={handleToggleFullscreen}
            />
          )}
        </View>
      </View>
    </Animated.View>
  )
}

/**
 * Control button component
 */
function ControlButton({
  active,
  activeLabel,
  inactiveLabel,
  activeIcon: ActiveIcon,
  inactiveIcon: InactiveIcon,
  onPress,
}: {
  active: boolean
  activeLabel: string
  inactiveLabel: string
  activeIcon: React.ComponentType<{fill: string; width: number}>
  inactiveIcon: React.ComponentType<{fill: string; width: number}>
  onPress: () => void
}) {
  const t = useTheme()

  if (IS_NATIVE) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={active ? activeLabel : inactiveLabel}
        onPress={onPress}
        style={[a.p_xs, a.rounded_full]}
        hitSlop={8}>
        {active ? (
          <ActiveIcon fill={t.palette.white} width={20} />
        ) : (
          <InactiveIcon fill={t.palette.white} width={20} />
        )}
      </Pressable>
    )
  }

  return (
    <PressableWithHover
      accessibilityRole="button"
      accessibilityLabel={active ? activeLabel : inactiveLabel}
      accessibilityHint=""
      onPress={onPress}
      style={[
        a.p_xs,
        a.rounded_full,
        web({transition: 'background-color 0.1s'}),
      ]}
      hoverStyle={{backgroundColor: 'rgba(255, 255, 255, 0.2)'}}>
      {active ? (
        <ActiveIcon fill={t.palette.white} width={20} aria-hidden />
      ) : (
        <InactiveIcon fill={t.palette.white} width={20} aria-hidden />
      )}
    </PressableWithHover>
  )
}

/**
 * Format time in seconds to mm:ss or hh:mm:ss
 */
function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}
